/**
 * LotSmith — public service status page.
 *
 * Hits /status on each worker (auth, license, billing, telegram) and
 * renders per-service health. No auth — this is the page traders share
 * when something feels off, so it must be reachable without being
 * signed in.
 *
 * The endpoint URLs default to the workers.dev subdomains; production
 * overrides via VITE_*_API_BASE so a custom api.lotsmith.app domain
 * works without a code change.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

interface Probe { ok: boolean; latencyMs: number }
interface StatusResponse {
  service: string;
  ok: boolean;
  checks: Record<string, Probe>;
  ts: string;
}

interface Service {
  id: string;
  label: string;
  url: string;
}

function svcUrl(envKey: string, fallback: string): string {
  const v = import.meta.env[envKey] as string | undefined;
  return (v && v !== "") ? v : fallback;
}

const SERVICES: Service[] = [
  { id: "auth",     label: "Auth",     url: svcUrl("VITE_AUTH_API_BASE",     "https://lotsmith-auth.workers.dev") + "/status" },
  { id: "license",  label: "License + Dashboard API", url: svcUrl("VITE_LICENSE_API_BASE", "https://lotsmith-license.workers.dev") + "/status" },
  { id: "billing",  label: "Billing",  url: svcUrl("VITE_BILLING_API_BASE",  "https://lotsmith-billing.workers.dev") + "/status" },
  { id: "telegram", label: "Telegram bot", url: svcUrl("VITE_TELEGRAM_API_BASE", "https://lotsmith-telegram.workers.dev") + "/status" },
];

type ServiceState =
  | { kind: "loading" }
  | { kind: "ok"; data: StatusResponse }
  | { kind: "down"; reason: string };

export function Status() {
  const [states, setStates] = useState<Record<string, ServiceState>>(
    Object.fromEntries(SERVICES.map((s) => [s.id, { kind: "loading" } as ServiceState])),
  );

  useEffect(() => {
    let cancelled = false;
    for (const s of SERVICES) {
      void (async () => {
        const started = Date.now();
        try {
          const r = await fetch(s.url, { method: "GET" });
          if (cancelled) return;
          if (!r.ok && r.status !== 503) {
            setStates((prev) => ({ ...prev, [s.id]: { kind: "down", reason: `HTTP ${r.status}` } }));
            return;
          }
          const j = await r.json() as StatusResponse;
          setStates((prev) => ({ ...prev, [s.id]: { kind: "ok", data: j } }));
          void started;
        } catch (err) {
          if (cancelled) return;
          setStates((prev) => ({ ...prev, [s.id]: { kind: "down", reason: err instanceof Error ? err.message : "fetch failed" } }));
        }
      })();
    }
    return () => { cancelled = true; };
  }, []);

  const overall = computeOverall(states);

  return (
    <div className="status">
      <header className="nav">
        <div className="nav__inner">
          <Link to="/" className="nav__brand">LotSmith</Link>
          <nav className="nav__links">
            <Link to="/pricing">Pricing</Link>
            <Link to="/login" className="btn btn--ghost">Sign in</Link>
          </nav>
        </div>
      </header>

      <section className="status__hero">
        <div className={`status__overall status__overall--${overall.tone}`}>
          <span className="status__dot" />
          <h1>{overall.label}</h1>
        </div>
        <p className="status__lead">
          Live probe of every LotSmith service. Refresh the page to re-check.
        </p>
      </section>

      <section className="status__grid">
        {SERVICES.map((s) => {
          const state = states[s.id] ?? { kind: "loading" } as ServiceState;
          return (
            <article key={s.id} className={`status__card status__card--${cardTone(state)}`}>
              <div className="status__svc">
                <span className="status__dot" />
                <span className="status__svc-label">{s.label}</span>
                <span className="status__svc-state">{cardLabel(state)}</span>
              </div>
              {state.kind === "ok" && (
                <div className="status__checks">
                  {Object.entries(state.data.checks).map(([name, probe]) => (
                    <div
                      key={name}
                      className={`status__check status__check--${probe.ok ? "ok" : "down"}`}
                    >
                      <span>{name}</span>
                      <span className="status__lat num">{probe.latencyMs}ms</span>
                    </div>
                  ))}
                </div>
              )}
              {state.kind === "down" && (
                <div className="status__error">{state.reason}</div>
              )}
            </article>
          );
        })}
      </section>

      <section className="status__legend">
        <p>
          Outages? Email <a href="mailto:support@lotsmith.app">support@lotsmith.app</a> with your account email and a screenshot. We reply within 12h on Pro / 6h on Trader / 1h on Team.
        </p>
      </section>
    </div>
  );
}

function cardTone(state: ServiceState): "loading" | "ok" | "down" {
  if (state.kind === "loading") return "loading";
  if (state.kind === "ok" && state.data.ok) return "ok";
  return "down";
}

function cardLabel(state: ServiceState): string {
  if (state.kind === "loading") return "checking…";
  if (state.kind === "ok") return state.data.ok ? "operational" : "degraded";
  return "down";
}

function computeOverall(states: Record<string, ServiceState>): { tone: "loading" | "ok" | "down" | "degraded"; label: string } {
  const values = Object.values(states);
  if (values.some((s) => s.kind === "loading")) return { tone: "loading", label: "Checking services…" };
  if (values.every((s) => s.kind === "ok" && s.data.ok)) return { tone: "ok", label: "All systems operational" };
  if (values.every((s) => s.kind === "down")) return { tone: "down", label: "Major outage" };
  return { tone: "degraded", label: "Partial outage" };
}
